import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { UsuariosService } from 'src/app/servicios/usuarios.service';
import { ToastService } from 'src/app/servicios/toast.service';

@Component({
  selector: 'app-modal-editar-usuario',
  templateUrl: './modal-editar-usuario.component.html',
  styleUrls: ['./modal-editar-usuario.component.scss']
})
export class ModalEditarUsuarioComponent implements OnInit {
  @Input() usuario: any;
  @Input() unidadId: number;
  @Output() cerrar = new EventEmitter<void>();
  @Output() usuarioActualizado = new EventEmitter<any>();

  public form: UntypedFormGroup;
  public cargando: boolean = false;
  public cargandoEditar: boolean = false;
  public cargandoReenviar: boolean = false;

  public tiposDocumento: string[] = [
    'Cédula de ciudadanía',
    'NIT',
    'Cédula Extranjería',
    'Tarjeta Identidad',
    'Pasaporte',
    'Registro Civil'
  ];

  constructor(
    private usuariosService: UsuariosService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();
  }

  private inicializarFormulario(): void {
    const nombres = this.usuario?.us_nombres || this.usuario?.name || this.usuario?.nombres || '';
    const apellidos = this.usuario?.us_apellidos || this.usuario?.last_name || this.usuario?.apellidos || '';
    const tipoDoc = this.normalizarTipoDocumento(this.usuario?.us_tipo_documento || this.usuario?.type_document || this.usuario?.tipo_documento);
    const numeroDoc = this.usuario?.us_numero_documento || this.usuario?.number_document || this.usuario?.numero_documento || '';
    const correo = this.usuario?.us_correo || this.usuario?.email || this.usuario?.correo || '';
    const indicativo = this.usuario?.us_indicativo || this.usuario?.indicative || this.usuario?.indicativo || '+57';
    const celular = this.usuario?.us_celular || this.usuario?.phone || this.usuario?.celular || '';
    const lugar = this.usuario?.lugar_render || this.usuario?.lugar_asignado || (this.usuario?.area_name && this.usuario?.site_name ? `${this.usuario.area_name} - ${this.usuario.site_name}` : (this.usuario?.site_name || ''));

    this.form = new UntypedFormGroup({
      nombres: new UntypedFormControl(nombres, [Validators.required]),
      apellidos: new UntypedFormControl(apellidos, [Validators.required]),
      tipoDocumento: new UntypedFormControl(tipoDoc, [Validators.required]),
      numeroDocumento: new UntypedFormControl(numeroDoc, [Validators.required]),
      correo: new UntypedFormControl(correo, [Validators.required, Validators.email]),
      indicativo: new UntypedFormControl(indicativo, [Validators.required]),
      celular: new UntypedFormControl(celular, [Validators.required]),
      lugar: new UntypedFormControl(lugar)
    });
  }

  private normalizarTipoDocumento(tipo: string): string {
    if (!tipo) return 'Cédula de ciudadanía';
    const upper = tipo.toUpperCase().trim();
    if (upper === 'CC' || upper.includes('CÉDULA') || upper.includes('CEDULA')) {
      if (upper.includes('EXTRANJER') || upper === 'CE') return 'Cédula Extranjería';
      return 'Cédula de ciudadanía';
    }
    if (upper === 'CE') return 'Cédula Extranjería';
    if (upper === 'TI') return 'Tarjeta Identidad';
    if (upper === 'PAS' || upper.includes('PASAPORTE')) return 'Pasaporte';
    if (upper === 'NIT') return 'NIT';
    if (upper === 'RC') return 'Registro Civil';
    return tipo;
  }

  public cerrarModal(): void {
    if (!this.cargando) {
      this.cerrar.emit();
    }
  }

  public editarUsuario(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.mostrarToast('Por favor diligencia todos los campos requeridos correctamente.');
      return;
    }

    const usId = Number(this.usuario?.id || this.usuario?.us_id || this.usuario?.user_id);
    if (!usId) {
      this.toastService.mostrarToast('No se encontró el ID del usuario a editar.');
      return;
    }

    const formVal = this.form.getRawValue();
    const datosEdicion = {
      id: usId,
      name: formVal.nombres ? formVal.nombres.trim() : '',
      last_name: formVal.apellidos ? formVal.apellidos.trim() : '',
      type_document: formVal.tipoDocumento,
      number_document: formVal.numeroDocumento ? String(formVal.numeroDocumento).trim() : '',
      indicative: formVal.indicativo ? formVal.indicativo.trim() : '+57',
      phone: formVal.celular ? String(formVal.celular).trim() : ''
    };

    this.cargando = true;
    this.cargandoEditar = true;
    this.usuariosService.editarTitular(datosEdicion).subscribe({
      next: (res: any) => {
        this.cargando = false;
        this.cargandoEditar = false;
        const mensaje = res?.data?.mensaje || res?.data?.message || res?.message || 'Usuario actualizado exitosamente.';
        this.toastService.mostrarToast(mensaje);

        const usuarioActualizado = {
          ...this.usuario,
          ...datosEdicion,
          us_nombres: datosEdicion.name,
          us_apellidos: datosEdicion.last_name,
          us_tipo_documento: datosEdicion.type_document,
          us_numero_documento: datosEdicion.number_document,
          us_indicativo: datosEdicion.indicative,
          us_celular: datosEdicion.phone,
          us_correo: formVal.correo ? formVal.correo.trim() : (this.usuario?.us_correo || this.usuario?.email || '')
        };
        this.usuarioActualizado.emit(usuarioActualizado);
        this.cerrarModal();
      },
      error: (err: any) => {
        this.cargando = false;
        this.cargandoEditar = false;
        console.error('Error al editar usuario:', err);
        const errorMsg = err?.error?.data?.mensaje || err?.error?.data?.message || err?.error?.message || 'Error al editar el usuario.';
        this.toastService.mostrarToast(errorMsg);
      }
    });
  }

  public reenviarCorreo(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.mostrarToast('Por favor diligencia todos los campos requeridos correctamente.');
      return;
    }

    const formVal = this.form.getRawValue();
    const usId = Number(this.usuario?.id || this.usuario?.us_id || this.usuario?.user_id);
    let urId = this.unidadId || this.usuario?.ur_id || this.usuario?.company_id;

    if (!urId) {
      try {
        const loginData = JSON.parse(localStorage.getItem('data_login') || '{}');
        urId = loginData?.company_id || 0;
      } catch (e) {
        urId = 0;
      }
    }

    if (!usId || !urId) {
      this.toastService.mostrarToast('No se encontró el ID del usuario o de la copropiedad.');
      return;
    }

    const nombreCompleto = `${formVal.nombres.trim()} ${formVal.apellidos.trim()}`;

    // Objeto visitante con toda la información requerida
    const visitante = {
      user_id: usId,
      name: nombreCompleto,
      first_name: formVal.nombres.trim(),
      last_name: formVal.apellidos.trim(),
      nombres: formVal.nombres.trim(),
      apellidos: formVal.apellidos.trim(),
      type_document: formVal.tipoDocumento,
      number_document: String(formVal.numeroDocumento).trim(),
      email: formVal.correo.trim(),
      correo: formVal.correo.trim(),
      indicative: formVal.indicativo ? formVal.indicativo.trim() : '+57',
      phone: String(formVal.celular).trim(),
      celular: String(formVal.celular).trim(),
      site_name: formVal.lugar ? formVal.lugar.trim() : (this.usuario?.site_name || ''),
      area_name: this.usuario?.area_name || '',
      note: this.usuario?.note || '',
      // Campos us_* mapeados
      us_nombres: formVal.nombres.trim(),
      us_apellidos: formVal.apellidos.trim(),
      us_correo: formVal.correo.trim(),
      us_celular: String(formVal.celular).trim(),
      us_numero_documento: String(formVal.numeroDocumento).trim(),
      us_tipo_documento: formVal.tipoDocumento
    };

    this.cargando = true;
    this.cargandoReenviar = true;
    this.usuariosService.reenviarCorreo(usId, urId, visitante).subscribe({
      next: (res: any) => {
        this.cargando = false;
        this.cargandoReenviar = false;
        const mensaje = res?.data?.message || res?.message || 'Correo reenviado exitosamente.';
        this.toastService.mostrarToast(mensaje);
        this.usuarioActualizado.emit({ ...this.usuario, ...visitante });
        this.cerrarModal();
      },
      error: (err: any) => {
        this.cargando = false;
        this.cargandoReenviar = false;
        console.error('Error al reenviar correo:', err);
        const errorMsg = err?.error?.data?.message || err?.error?.message || 'Error al reenviar el correo.';
        this.toastService.mostrarToast(errorMsg);
      }
    });
  }

  public guardarYReenviar(): void {
    this.reenviarCorreo();
  }
}
